import { getNodeTagsMap } from "@features/administration/products/EditProduct/nodeTagsHelper"
import { useGraph } from "@features/builder/graph/graphAtoms"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { TagsFieldRow } from "@features/builder/NodeDrawer/NodeForm/TagsFieldRow"
import { Add, Info } from "@mui/icons-material"
import { FormHelperText, Grid, IconButton, Tooltip } from "@mui/material"
import { useGetNodeTagsQuery } from "@store/services/productsService"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"
import { useMemo } from "react"
import { useFieldArray, useFormContext, useFormState } from "react-hook-form"

type NodeTagsFieldProps = {
  nodeId: string
}

export const NodeTagsField = ({ nodeId }: NodeTagsFieldProps) => {
  const { graphId } = useGraph()
  const form = useFormContext<NodeFormValues>()
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "tags" })
  const { errors } = useFormState({ control: form.control, name: "tags" })
  const [organisationKey, divisionKey, productKey] = (graphId ?? "").split("#")
  const errorMessage = errors.tags?.root?.message ?? ""

  const { getNodeValidation } = useNodesValidation()
  const validationErrors = getNodeValidation(nodeId, "NODE_TAGS")?.errors ?? []

  const { data: getTagsResponse } = useGetNodeTagsQuery(
    {
      key: productKey ?? "",
      orgKey: organisationKey ?? "",
      divKey: divisionKey ?? "",
    },
    {
      skip: !productKey || !organisationKey || !divisionKey,
    },
  )

  const tagsMap = useMemo(() => getNodeTagsMap(getTagsResponse ?? ""), [getTagsResponse])

  return (
    <fieldset style={{ color: "rgba(0, 0, 0, 0.6)", borderColor: "rgba(0, 0, 0, 0.6)" }}>
      <legend>
        Node level tags
        <Tooltip
          title={`Node level tags are tags that are defined on the product level
                    and can be used in all graphs within the product.
                    Depending on the tag type, you may choose option for the tag or enter text value.`}
        >
          <Info fontSize="small" sx={{ verticalAlign: "bottom", ml: 1 }} />
        </Tooltip>
      </legend>
      <Grid container direction="column" spacing={1} wrap="nowrap">
        {fields.map((tagField, index) => (
          <TagsFieldRow
            key={tagField.id}
            index={index}
            onDelete={() => remove(index)}
            tagsMap={tagsMap}
            validationErrors={validationErrors}
          />
        ))}

        {errorMessage && (
          <Grid item>
            <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
              {errorMessage}
            </FormHelperText>
          </Grid>
        )}

        <Grid item>
          <Tooltip title="Add Tag">
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={form.formState.disabled}
                onClick={() => append({ tag: "", value: "" })}
              >
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </fieldset>
  )
}
